import { useState, useRef } from 'react';
import { Download, Upload, Trash2, ShieldAlert, Loader2, CheckCircle, AlertCircle, X, Database } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const ConfirmModal = ({ title, message, danger = false, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-sm w-full max-w-md p-6 shadow-xl">
      <div className={`flex items-center gap-3 mb-4 ${danger ? 'text-red-600' : 'text-orange-600'}`}>
        <ShieldAlert className="h-6 w-6 flex-shrink-0" />
        <h3 className="text-xl font-bold uppercase tracking-tight">{title}</h3>
      </div>
      <p className="text-slate-600 text-sm mb-6 leading-relaxed">{message}</p>
      <div className="flex gap-3 justify-end">
        <button onClick={onCancel} disabled={loading}
          className="px-5 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 text-sm font-medium">
          Cancel
        </button>
        <button onClick={onConfirm} disabled={loading}
          className={`px-5 py-2 text-white rounded-sm text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50 ${danger ? 'bg-red-500 hover:bg-red-600' : 'bg-orange-500 hover:bg-orange-600'}`}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Confirm
        </button>
      </div>
    </div>
  </div>
);

export const DataManagement = () => {
  const [loading, setLoading] = useState('');
  const [toast, setToast] = useState(null); // { type: 'success'|'error', msg }
  const [confirm, setConfirm] = useState(null); // { key, title, message, danger }
  const [deleteResult, setDeleteResult] = useState(null);
  const fileRef = useRef();

  const DELETE_SCOPES = [
    { key: 'inward_inventory', label: 'Inward / Inventory / Internal Transfer / Part Sale Tracker' },
    { key: 'purchase', label: 'Purchase & Expenses' },
    { key: 'sale', label: 'Sale' },
    { key: 'transactions', label: 'Transactions' },
    { key: 'all', label: 'All Transactional Data', isAll: true },
  ];

  const handleDeleteTransactional = async (scope) => {
    setLoading('del_' + scope);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/delete-transactional/${scope}`, getAuthHeaders());
      setDeleteResult({ success: true, message: 'Deleted successfully.' });
    } catch (err) {
      setDeleteResult({ success: false, message: err.response?.data?.detail || 'Delete failed' });
    } finally {
      setLoading('');
      setConfirm(null);
    }
  };

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Export ──────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    setLoading('export');
    try {
      const res = await axios.get(`${BACKEND_URL}/api/data-management/export`, {
        ...getAuthHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const link = document.createElement('a');
      link.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `tejas-data-export-${ts}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast('success', 'Data exported successfully');
    } catch {
      showToast('error', 'Export failed. Please try again.');
    } finally {
      setLoading('');
    }
  };

  // ── Import ──────────────────────────────────────────────────────────────────
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setLoading('import');
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${BACKEND_URL}/api/data-management/import`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      showToast('success', 'Data imported successfully');
    } catch (err) {
      showToast('error', err.response?.data?.detail || 'Import failed. Check your JSON file.');
    } finally {
      setLoading('');
    }
  };

  // ── Clear Master ────────────────────────────────────────────────────────────
  const handleClearMaster = async () => {
    setConfirm(null);
    setLoading('clear-master');
    try {
      await axios.delete(`${BACKEND_URL}/api/data-management/clear-master`, getAuthHeaders());
      showToast('success', 'Master data cleared successfully');
    } catch {
      showToast('error', 'Failed to clear master data');
    } finally {
      setLoading('');
    }
  };

  // ── Clear All ───────────────────────────────────────────────────────────────
  const handleClearAll = async () => {
    setConfirm(null);
    setLoading('clear-all');
    try {
      await axios.delete(`${BACKEND_URL}/api/data-management/clear-all`, getAuthHeaders());
      showToast('success', 'All data cleared (superadmin login preserved)');
    } catch {
      showToast('error', 'Failed to clear data');
    } finally {
      setLoading('');
    }
  };

  const actions = [
    {
      key: 'export',
      icon: Download,
      label: 'Export Data',
      description: 'Download all data (master + transactional) as a single JSON file. Use this to back up your data or migrate to another instance.',
      btnLabel: 'Export JSON',
      btnClass: 'bg-blue-600 hover:bg-blue-700 text-white',
      iconClass: 'text-blue-500',
      bgClass: 'bg-blue-50 border-blue-200',
      onClick: handleExport,
    },
    {
      key: 'import',
      icon: Upload,
      label: 'Import Data',
      description: 'Import data from a previously exported JSON file. Existing records are not deleted — new records are added on top. Duplicate IDs may cause conflicts.',
      btnLabel: 'Choose JSON File',
      btnClass: 'bg-green-600 hover:bg-green-700 text-white',
      iconClass: 'text-green-500',
      bgClass: 'bg-green-50 border-green-200',
      onClick: () => fileRef.current?.click(),
    },
    {
      key: 'clear-master',
      icon: Trash2,
      label: 'Clear Master Data',
      description: 'Deletes all master data: companies, branches, brands, categories, sub-categories, components, specs, racks, taxes, units, vendors, customers, payment terms, payment modes, warranties, service items, and invoice settings. Transactional data (invoices, orders, etc.) is NOT cleared.',
      btnLabel: 'Clear Master Data',
      btnClass: 'bg-orange-500 hover:bg-orange-600 text-white',
      iconClass: 'text-orange-500',
      bgClass: 'bg-orange-50 border-orange-200',
      onClick: () => setConfirm({
        key: 'clear-master',
        title: 'Clear Master Data',
        message: 'This will permanently delete all master data (companies, vendors, customers, categories, products config, etc.) and invoice settings. Invoices, orders and transactions will remain. This cannot be undone.',
        danger: false,
        onConfirm: handleClearMaster,
      }),
    },
    {
      key: 'clear-all',
      icon: ShieldAlert,
      label: 'Clear All Data',
      description: 'Deletes EVERYTHING — master data, products, inventory, invoices, orders, purchase bills, payments, leads, tickets, and all transactions. Your superadmin login will be preserved.',
      btnLabel: 'Clear All Data',
      btnClass: 'bg-red-500 hover:bg-red-600 text-white',
      iconClass: 'text-red-500',
      bgClass: 'bg-red-50 border-red-200',
      onClick: () => setConfirm({
        key: 'clear-all',
        title: 'Clear All Data',
        message: 'This will permanently delete ALL data in the system — master data, inventory, invoices, purchases, payments, services, and more. Only your superadmin login will be preserved. This CANNOT be undone.',
        danger: true,
        onConfirm: handleClearAll,
      }),
    },
  ];

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <Database className="h-7 w-7 text-orange-500" />
        <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Config Settings</h1>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-sm shadow-lg border text-sm font-medium transition-all
          ${toast.type === 'success' ? 'bg-green-50 border-green-300 text-green-800' : 'bg-red-50 border-red-300 text-red-800'}`}>
          {toast.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {toast.msg}
          <button onClick={() => setToast(null)} className="ml-2 text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Hidden file input */}
      <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={handleImportFile} />

      {/* Action Cards */}
      <div className="flex flex-col gap-5">
        {actions.map(a => {
          const Icon = a.icon;
          const isLoading = loading === a.key;
          return (
            <div key={a.key} className={`rounded-sm border p-6 flex flex-col md:flex-row md:items-center gap-5 ${a.bgClass}`}>
              <div className={`flex-shrink-0 ${a.iconClass}`}>
                <Icon className="h-9 w-9" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-slate-900 uppercase tracking-tight mb-1">{a.label}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{a.description}</p>
              </div>
              <div className="flex-shrink-0">
                <button
                  onClick={a.onClick}
                  disabled={!!loading}
                  data-testid={`btn-${a.key}`}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-sm text-sm font-bold uppercase tracking-wider disabled:opacity-50 transition-colors ${a.btnClass}`}
                >
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
                  {isLoading ? 'Working…' : a.btnLabel}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Confirm Modal */}
      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          danger={confirm.danger}
          loading={!!loading}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {/* Danger Zone */}
      <div className="mt-10 border border-red-200 rounded-sm bg-red-50 p-6">
        <div className="flex items-center gap-2 mb-1">
          <ShieldAlert className="h-5 w-5 text-red-600" />
          <h2 className="text-lg font-bold text-red-700 uppercase tracking-wider">Danger Zone</h2>
        </div>
        <p className="text-sm text-red-600 mb-5">Permanently delete transactional data. Master data (products, vendors, customers, companies, etc.) will NOT be affected.</p>

        {deleteResult && (
          <div className={`mb-4 px-4 py-3 rounded-sm text-sm font-medium flex items-center justify-between ${deleteResult.success ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'}`}>
            <span>{deleteResult.message}</span>
            <button onClick={() => setDeleteResult(null)} className="ml-4 text-current opacity-60 hover:opacity-100 text-lg leading-none">&times;</button>
          </div>
        )}

        <div className="space-y-3">
          {DELETE_SCOPES.map(scope => (
            <div key={scope.key} className="flex items-center justify-between bg-white border border-red-100 rounded-sm px-4 py-3">
              <span className="text-sm font-medium text-slate-700">{scope.label}</span>
              <button
                onClick={() => setConfirm({
                  title: 'Delete ' + scope.label,
                  message: `This will permanently delete all "${scope.label}" data. Master data will remain intact. This cannot be undone.`,
                  danger: true,
                  onConfirm: () => handleDeleteTransactional(scope.key),
                })}
                disabled={!!loading}
                data-testid={`delete-scope-${scope.key}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors disabled:opacity-50 ${scope.isAll ? 'bg-red-600 text-white hover:bg-red-700' : 'border border-red-300 text-red-600 hover:bg-red-50'}`}>
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
